import { NativeSelectOption } from "@bednbody/ui/components/native-select";
import { NativeSelect } from "@bednbody/ui/components/native-select";
import { Badge } from "@bednbody/ui/components/badge";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Input } from "@bednbody/ui/components/input";
import type { ColumnDef } from "@tanstack/react-table";
import { PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { EdgeFunctionError } from "@/lib/edge-functions";
import { isServiceDate, isoWeekOf } from "../iso-week";
import {
  useGenerateRiderSessions,
  useRiderSessionBatches,
  type RiderSessionBatchRow,
} from "../rider-sessions.hooks";
import { Callout } from "@bednbody/ui/components/callout";

interface BatchesPanelProps {
  hubId: string | null;
  hubs: { id: string; name: string }[];
  canGenerate: boolean;
}

/**
 * CORE-05B supply — what has been generated, and the control that generates it.
 *
 * ⚠ THE NOTE THAT USED TO SIT HERE WAS FALSE ON BOTH COUNTS. It said "**Nothing
 * schedules generation** (OQ-66). CORE-05A's Friday 7:00 PM count lock does not
 * exist" — and the card repeated it to operators until 2026-09-17.
 *
 *   · `0127_rider_session_generation_cron.sql` — "OQ-66: Rider Session
 *     generation actually runs on Saturday" — installs the `pg_cron` job
 *     `rider-session-generate` at `0 16 * * 5` (Saturday 00:00 Manila).
 *     Verified active on the deployed database, 2026-09-06, alongside
 *     `rider-session-generate-watchdog`.
 *   · The count lock shipped as `therapist_final_counts` in
 *     `0076_therapist_session_finalization.sql`.
 *
 * So this button is a MANUAL RE-RUN beside a schedule, not the only way it ever
 * happens. A duplicate batch is refused by the database rather than by this
 * screen: `rider_session_batch_generation_key` is unique on (cycle_id, hub_id,
 * service_date, count_lock_id, template_version) — 0062:134. Whether a re-click
 * after the cron surfaces as an error or is swallowed is a UI question nobody
 * has answered; do not assume it is silent.
 */
export function BatchesPanel({ hubId, hubs, canGenerate }: BatchesPanelProps) {
  const { data: batches, isLoading, isError } = useRiderSessionBatches(hubId);
  const generate = useGenerateRiderSessions();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ hubId: hubId ?? "", serviceDate: "" });

  const valid = form.hubId !== "" && isServiceDate(form.serviceDate);
  const columns: ColumnDef<RiderSessionBatchRow, unknown>[] = [
    {
      id: "date",
      accessorFn: (batch) => batch.service_date,
      header: "Batch",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.service_date}
          subtitle={`${isoWeekOf(row.original.service_date)} · ${row.original.hubs?.name ?? "No hub"}`}
          fallback="RS"
        />
      ),
    },
    { id: "hub", accessorFn: (batch) => batch.hubs?.name ?? "", header: "Hub" },
    { id: "therapists", accessorFn: (batch) => batch.final_therapist_count, header: "Therapists" },
    { id: "total", accessorFn: (batch) => batch.required_total, header: "Total" },
    { id: "a", accessorFn: (batch) => batch.session_a, header: "A" },
    { id: "b", accessorFn: (batch) => batch.session_b, header: "B" },
    { id: "c", accessorFn: (batch) => batch.session_c, header: "C" },
    {
      id: "origin",
      accessorFn: (batch) => batch.origin,
      header: "Origin",
      cell: ({ row }) =>
        row.original.origin === "emergency_override" ? (
          <Badge variant="warning">Manual</Badge>
        ) : (
          <Badge variant="muted">Automatic</Badge>
        ),
    },
    {
      id: "generated",
      accessorFn: (batch) => batch.generated_at,
      header: "Generated",
      cell: ({ row }) =>
        new Date(row.original.generated_at).toLocaleString("en-PH", {
          dateStyle: "medium",
          timeStyle: "short",
        }),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          label={row.original.service_date}
          actions={[
            {
              label: "Copy batch ID",
              onSelect: () => void navigator.clipboard.writeText(row.original.id),
            },
          ]}
        />
      ),
    },
  ];

  function submit() {
    if (!valid) return;
    // ⚠ THERE IS NO COUNT FIELD. The server reads the Friday 7:00 PM lock
    // (TSESSION-069; OQ-66 and OQ-70), so supply cannot be set by hand. The field
    // that used to sit here was required, never sent, and drove a "Will generate
    // N" preview that could disagree with what the server actually made.
    generate.mutate(
      { hubId: form.hubId, serviceDate: form.serviceDate },
      { onSuccess: () => setOpen(false) },
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between gap-3">
        <div>
          <h2 className="type-title-subsection">Generation batches</h2>
          <h4 className="type-body-default max-w-prose">
            Session totals come from the approved CORE-05B calculation —{" "}
            <span className="font-medium text-foreground">ceil(therapists ÷ 4)</span>, split across
            Sessions A, B and C by the weekday or weekend template. The Therapist count locked on
            Friday at 7:00 PM is the only input.
          </h4>
        </div>

        {canGenerate ? (
          <Button
            onClick={() => {
              generate.reset();
              setForm({ hubId: hubId ?? "", serviceDate: "" });
              setOpen(true);
            }}
          >
            <PlusIcon weight="bold" /> Generate a batch
          </Button>
        ) : null}
      </div>

      <Callout>
        Generation runs automatically every Saturday at 12:00 AM (Manila). Generating by hand only
        re-runs a date the schedule missed — it reads the same locked count and never adds supply.
      </Callout>

      {isError ? (
        <p className="text-sm text-destructive">
          Couldn&rsquo;t load batches — check your connection and try again.
        </p>
      ) : (
        <AdminDataTable
          presentation="standalone"
          data={batches ?? []}
          columns={columns}
          getRowId={(batch) => batch.id}
          searchText={(batch) => [batch.service_date, batch.hubs?.name, batch.origin].join(" ")}
          searchPlaceholder="Search date, hub, or origin…"
          facets={[
            {
              id: "origin",
              label: "Origin",
              options: [
                { value: "scheduled", label: "Automatic" },
                { value: "emergency_override", label: "Manual" },
              ],
              getValue: (batch) => batch.origin,
            },
          ]}
          initialSorting={[{ id: "date", desc: true }]}
          loading={isLoading}
          emptyTitle="No batches yet"
          emptyDescription="Nothing has been generated for this hub."
          exportFilename="rider-session-batches"
          exportColumns={[
            { header: "Service date", value: (batch) => batch.service_date },
            { header: "Hub", value: (batch) => batch.hubs?.name },
            { header: "Therapists", value: (batch) => batch.final_therapist_count },
            { header: "Total", value: (batch) => batch.required_total },
            { header: "A", value: (batch) => batch.session_a },
            { header: "B", value: (batch) => batch.session_b },
            { header: "C", value: (batch) => batch.session_c },
            { header: "Origin", value: (batch) => batch.origin },
            { header: "Generated", value: (batch) => batch.generated_at },
          ]}
        />
      )}

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) setOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Rider Sessions</DialogTitle>
            <DialogDescription>
              One batch for one hub and one service date, from that week&rsquo;s locked Therapist
              count. Re-running a date that already has a batch returns it instead of creating a
              second one.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label htmlFor="gen-hub" className="block text-sm font-medium">
                Hub
              </label>
              <NativeSelect
                id="gen-hub"
                value={form.hubId}
                onChange={(e) => setForm((f) => ({ ...f, hubId: e.target.value }))}
                className="w-full"
              >
                <NativeSelectOption value="">Select a hub…</NativeSelectOption>
                {hubs.map((hub) => (
                  <NativeSelectOption key={hub.id} value={hub.id}>
                    {hub.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </div>

            <div className="space-y-2">
              <label htmlFor="gen-date" className="block text-sm font-medium">
                Service date
              </label>
              <Input
                id="gen-date"
                type="date"
                value={form.serviceDate}
                onChange={(e) => setForm((f) => ({ ...f, serviceDate: e.target.value }))}
              />
              {isServiceDate(form.serviceDate) ? (
                <p className="text-xs text-muted-foreground">
                  Cycle {isoWeekOf(form.serviceDate)} · weekday and weekend dates use different
                  A/B/C templates.
                </p>
              ) : null}
            </div>

            {generate.isError ? (
              <p className="text-sm text-destructive">
                {generate.error instanceof EdgeFunctionError
                  ? generate.error.message
                  : (generate.error as Error).message}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={generate.isPending}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!valid || generate.isPending}>
              {generate.isPending ? "Generating…" : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {generate.isSuccess && generate.data ? (
        <p className="text-sm text-muted-foreground">
          <Badge variant={generate.data.alreadyExisted ? "muted" : "success"}>
            {generate.data.alreadyExisted ? "Already generated" : "Generated"}
          </Badge>{" "}
          {generate.data.requiredTotal} Sessions — A {generate.data.sessionA} · B{" "}
          {generate.data.sessionB} · C {generate.data.sessionC}
        </p>
      ) : null}
    </div>
  );
}
